import React, { Component } from "react";
import PropTypes from "prop-types";
import { reduxForm } from "redux-form";
import moment from "moment";
import CheckboxInput from "./CheckboxInput";
import WizardFooter from "./WizardFooter";
import SignatureModal from "./SignatureModal";
import EnglishWarningModal from "./EnglishWarningModal";
import AlmostDoneModal from "./AlmostDoneModal";
import { felonyConsideration } from "../utils/surveyLogic";

export const fields = [
  "first_name",
  "last_name",
  "ssn",
  "birthMonth",
  "birthDay",
  "birthYear",
  "email",
  "phone",
  "street",
  "city",
  "county",
  "state",
  "zip",
  "worked_for_us_before",
  "is_veteran",
  "is_entitled_to_compensation",
  "dischargeMonth",
  "dischargeYear",
  "service_branch",
  "unemployed_four_weeks",
  "unemployed_six_months",
  "referred_by_vocal_rehab",
  "referred_by_ticket_to_work",
  "referred_by_veterans_affairs",
  "supplemental_security_income",
  "convicted_of_felony",
  "convictionMonth",
  "convictionYear",
  "releaseMonth",
  "releaseYear",
  "state_or_federal_conviction",
  "court_city_or_county",
  "snap_six_months",
  "tanf_three_years",
  "snap_one_year",
  "benefits_recipient",
  "benefits_city_and_state",
  "has_been_unemployed",
  "has_received_unemployment_compensation",
  "unemploymentStartMonth",
  "unemploymentStartDay",
  "unemploymentStartYear",
  "unemployment_state",
  "summary_signature",
  "irs1",
  "irs2",
  "irs3",
  "irs4",
  "irs5",
  "irs6",
  "irs7",
  "irs_signature",
  "irsSignatureDay",
  "irsSignatureMonth",
  "irsSignatureYear",
  "irs_signature_statement",
];

const validate = values => {
  const errors = {};
  if (!values.irs_signature) {
    errors.irs_signature = "Signature required";
  }
  return errors;
};

const buildBirthday = ({ birthYear, birthMonth, birthDay }) =>
  moment(`${birthYear.value}-${birthMonth.value}-${birthDay.value}`);

export class WizardFormIRS8850 extends Component {
  state = {
    showAlmostDone: true,
    formEnglishOverride: false,
    showEnglishWarning: false,
    showSignature: false,
  };

  componentDidMount() {
    const {
      fields: {
        birthMonth,
        birthDay,
        birthYear,
        is_veteran,
        is_entitled_to_compensation,
        dischargeMonth,
        dischargeYear,
        snap_six_months,
        tanf_three_years,
        snap_one_year,
        referred_by_vocal_rehab,
        referred_by_ticket_to_work,
        referred_by_veterans_affairs,
        supplemental_security_income,
        convicted_of_felony,
        convictionMonth,
        convictionYear,
        releaseMonth,
        releaseYear,
        unemployed_four_weeks,
        unemployed_six_months,
        has_been_unemployed,
        has_received_unemployment_compensation,
        irs1,
        irs2,
        irs3,
        irs4,
        irs5,
        irs6,
        irs7,
      },
    } = this.props;

    const dischargeMoment = moment(
      `${dischargeYear.value}-${dischargeMonth.value}-01`
    );

    const monthsSinceDischarge = moment
      .duration(moment() - dischargeMoment)
      .asMonths();

    irs1.autofill(false);

    let birthMoment, age;
    birthMoment = buildBirthday({ birthYear, birthMonth, birthDay });
    age = moment.duration(moment() - birthMoment).years();

    irs2.autofill(
      tanf_three_years.value === "yes" ||
        (is_veteran.value === "yes" &&
          (snap_six_months.value === "yes" || snap_one_year.value === "yes")) ||
        (referred_by_vocal_rehab.value === "yes" ||
          referred_by_ticket_to_work.value === "yes" ||
          referred_by_veterans_affairs.value === "yes") ||
        (age >= 18 &&
          age < 40 &&
          (snap_six_months.value === "yes" || snap_one_year.value === "yes")) ||
        felonyConsideration({
          convicted_of_felony: convicted_of_felony.value,
          convictionMonth: convictionMonth.value,
          convictionYear: convictionYear.value,
          releaseMonth: releaseMonth.value,
          releaseYear: releaseYear.value,
        }) ||
        (is_veteran.value === "yes" &&
          (unemployed_six_months.value === "yes" ||
            has_been_unemployed.value === "true" ||
            unemployed_four_weeks.value === "yes")) ||
        supplemental_security_income.value === "yes"
    );
    irs3.autofill(
      is_veteran.value === "yes" &&
        (unemployed_six_months.value === "yes" ||
          has_been_unemployed.value === "true")
    );
    irs4.autofill(
      is_veteran.value === "yes" &&
        is_entitled_to_compensation.value === "yes" &&
        monthsSinceDischarge <= 13
    );
    irs5.autofill(
      is_veteran.value === "yes" &&
        is_entitled_to_compensation.value === "yes" &&
        unemployed_six_months.value === "yes"
    );
    irs6.autofill(tanf_three_years.value === "yes");
    irs7.autofill(
      has_been_unemployed.value === "true" &&
        has_received_unemployment_compensation.value === "true"
    );
  }

  closeAlmostDone = () => {
    this.setState({ showAlmostDone: false });
  };

  closeEnglishWarning = () => {
    this.setState({ showEnglishWarning: false });
  };

  switchToEnglish = () => {
    this.setState({
      showEnglishWarning: false,
      formEnglishOverride: true,
      showSignature: true,
    });
  };

  closeSignature = () => {
    this.setState({
      showSignature: false,
      formEnglishOverride: false,
    });
  };

  openSignature = () => {
    if (this.props.language === "english") {
      this.setState({ showSignature: true });
    } else {
      this.setState({ showEnglishWarning: true });
    }
  };

  getText = () => {
    const { allText } = this.props;
    return { ...allText.shared, ...allText.irs, ...allText };
  };

  getFormText = () => {
    const { allText, allTextEnglish } = this.props;
    const formAllText = this.state.formEnglishOverride
      ? allTextEnglish
      : allText;
    return {
      ...formAllText.shared,
      ...formAllText.irs,
      ...formAllText,
    };
  };

  render() {
    const {
      fields: {
        first_name,
        last_name,
        ssn,
        birthMonth,
        birthDay,
        birthYear,
        phone,
        street,
        city,
        county,
        state,
        zip,
        irs1,
        irs2,
        irs3,
        irs4,
        irs5,
        irs6,
        irs7,
        irs_signature,
        irsSignatureDay,
        irsSignatureMonth,
        irsSignatureYear,
        irs_signature_statement,
      },
      handleSubmit,
      previousPage,
    } = this.props;
    const text = this.getText();
    const formText = this.getFormText();
    const progress = 1;
    const birthMoment = buildBirthday({ birthYear, birthMonth, birthDay });
    const age = moment.duration(moment() - birthMoment).years();
    const irsFields = [irs1, irs2, irs3, irs4, irs5, irs6, irs7];
    return (
      <form onSubmit={handleSubmit} className="form-horizontal">
        <div className="wizard-page-content summary irs">
          <table className="form-header">
            <tbody>
              <tr>
                <td>
                  {"Form "}
                  <span className="form-id">{"8850"}</span>
                  <br />
                  {"(Rev. March 2016)"}
                  <br />
                  {"Department of the Treasury"}
                  <br />
                  {"Internal Revenue Service"}
                  <br />
                </td>
                <td>
                  <div className="form-title">
                    {
                      "Pre-Screening Notice and Certification Request for the Work\
                      Opportunity Credit"
                    }
                  </div>
                  {
                    "▶ Information about Form IRS and its separate instructions is\
                    at www.irs.gov/form8850."
                  }
                </td>
                <td>{"OMB No. 1545-1500"}</td>
              </tr>
            </tbody>
          </table>

          <div className="instruction">
            <strong>{formText.jobApplicant}</strong>
          </div>

          <div className="row employee-info-summaryIRS">
            <div className="col-12 col-sm-7">
              <div>
                <div>{formText.name}</div>
                <div className="employee-value" id="full_name">
                  {first_name.value} {last_name.value}
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-5">
              <div>
                <div>
                  {formText.ssn}
                  {" ▶"}
                </div>
                <div className="employee-value">{ssn.value}</div>
              </div>
            </div>
            <div className="col-12">
              <div>
                <div>{formText.street}</div>
                <div className="employee-value">{street.value}</div>
              </div>
            </div>
            <div className="col-12">
              <div>
                <div>{formText.cityStateZip}</div>
                <div className="employee-value">{`${city.value}, ${
                  state.value
                }, ${zip.value}`}</div>
              </div>
            </div>
            <div className="col-6 col-sm-7">
              <div>
                <div>{formText.county}</div>
                <div className="employee-value">{county.value}</div>
              </div>
            </div>
            <div className="col-6 col-sm-5">
              <div>
                <div>{formText.phone}</div>
                <div className="employee-value">{phone.value}</div>
              </div>
            </div>
            <div className="col-12 col-sm-10 col-md-9 col-lg-8">
              <div>
                <div>{formText.birthDate}</div>
                <div className="employee-value test-irs-dob">
                  {age < 40 && birthMoment.format("MMM D, YYYY")}
                </div>
              </div>
            </div>
          </div>

          <div className="separator">
            <hr />
          </div>

          <table className="items">
            <tbody>
              {formText.items.map((item, index) => (
                <tr key={item.text}>
                  <td>{index + 1}</td>
                  <td>
                    <CheckboxInput field={irsFields[index]} disabled={false} />
                  </td>
                  <td>
                    {item.text}
                    {item.items && (
                      <ul>
                        {item.items.map(subItem => (
                          <li key={subItem.text}>
                            {subItem.text}
                            {subItem.items && (
                              <ol type="a">
                                {subItem.items.map(subSubItem => (
                                  <li key={subSubItem.text}>
                                    {subSubItem.text}
                                  </li>
                                ))}
                              </ol>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="all-applicants">{formText.allApplicantsMustSign}</div>
          <div className="under-penalties">{formText.underPenalties}</div>
          <div className="row job-applicants-signature">
            <div className="col-12 col-sm-9">
              {formText.jobApplicantsSignature}
              {" ▶"}
            </div>
            <div className="col-12 col-sm-3">
              <span className="date-label">{formText.date}</span>
              <span className="date-value">
                {moment().format("MMM D, YYYY")}
              </span>
            </div>
          </div>
          <div className="row for-privacy">
            <div className="col-12 col-sm-7">{formText.forPrivacy}</div>
            <div className="col-6 col-sm-2">{"Cat. No. 22851L"}</div>
            <div className="col-6 col-sm-3">
              {"Form "}
              <span className="form-id">{"8850"}</span>
              {" (Rev. 3-2016)"}
            </div>
          </div>
        </div>
        <WizardFooter
          text={formText}
          previousPage={previousPage}
          nextPage={this.openSignature}
          progress={progress}
          previousLabel={formText.startOver}
          nextLabel={formText.sign}
          max={3}
          what={formText.signed}
        />

        <SignatureModal
          showSignature={this.state.showSignature}
          openSignature={this.openSignature}
          closeSignature={this.closeSignature}
          handleSubmit={handleSubmit}
          signatureField={irs_signature}
          signatureDay={irsSignatureDay}
          signatureMonth={irsSignatureMonth}
          signatureYear={irsSignatureYear}
          signatureStatement={irs_signature_statement}
          text={text}
          formText={formText}
          formEnglishOverride={this.state.formEnglishOverride}
        />

        <EnglishWarningModal
          show={this.state.showEnglishWarning}
          close={this.closeEnglishWarning}
          switchToEnglish={this.switchToEnglish}
          text={text}
        />

        <AlmostDoneModal
          show={this.state.showAlmostDone}
          close={this.closeAlmostDone}
          text={text}
        />
      </form>
    );
  }
}

WizardFormIRS8850.propTypes = {
  allText: PropTypes.object.isRequired,
  allTextEnglish: PropTypes.object,
  fields: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  previousPage: PropTypes.func.isRequired,
};

export default reduxForm({
  form: "wizard",
  fields,
  destroyOnUnmount: false,
  validate,
})(WizardFormIRS8850);



// WEBPACK FOOTER //
// ./src/components/WizardFormIRS8850.js